/**
 * DynamoDB Schema Types for Co-op Raid System
 *
 * Defines the structure of all raid-related entries stored in DynamoDB.
 *
 * Table Structure:
 * - PK: raid_id
 * - SK: data_type (RAID_DATA | ROUND#X:STEP#Y | CONNECTION#player_X)
 */

import { RaidDifficulty, RaidRoomOption } from "./raids";

// ============================================================================
// Enums & Constants
// ============================================================================

export type RaidStatus =
  | "lobby" // Waiting for players to join and ready up
  | "starting" // All players ready, raid is starting
  | "room_selection" // Players voting on next room
  | "draft" // Players selecting pokemon for battle
  | "battle" // Battle in progress
  | "battle_results" // Showing battle results, waiting for ACK
  | "victory" // Raid completed successfully
  | "defeat" // All player pokemon fainted
  | "abandoned"; // A player quit the raid

export type StepState = "PENDING" | "IN_PROGRESS" | "COMPLETED" | "TIMEOUT";

export type RaidOutcome = "victory" | "defeat";

export type PlayerRole = "player_a" | "player_b" | "player_c" | "player_d";

export const PLAYER_ROLES: PlayerRole[] = [
  "player_a",
  "player_b",
  "player_c",
  "player_d",
];

// ============================================================================
// Player & Pokemon Types
// ============================================================================

/**
 * Player data structure as stored in raid
 */
export interface RaidPlayer {
  id: string;
  telegram_id: string | null;
  username: string;
  country: string | null;
  team: {
    id: number;
    name: string;
    pokemon: RaidPokemon[];
  };
  /** Whether this player is ready to start (lobby phase) */
  is_ready: boolean;
}

/**
 * Pokemon data structure as stored in raid.
 * HP fields are populated after raid starts.
 */
export interface RaidPokemon {
  id: number;
  display_id: string;
  owner_id: string;
  base_pokemon_name: string;
  name: string | null;
  level: number;
  talents: string[];
  active_skills: string[];
  /** Max HP - populated when raid starts */
  max_hp?: number;
  /** Current HP - updated after each battle */
  current_hp?: number;
  /** Whether this pokemon has fainted */
  is_fainted?: boolean;
}

/**
 * Battle result stored in raid battles array
 */
export interface RaidBattleResult {
  /** Round index where this battle occurred (0-10, where 10 is boss) */
  round_index: number;

  /** S3 key for the complete battle log JSON */
  battle_log_s3_key: string;

  /** Outcome of this battle */
  outcome: RaidOutcome;

  /** Duration of battle in seconds */
  duration_seconds: number;

  /** Pokemon IDs used in this battle (combined team from all players) */
  team_pokemon_ids: number[];

  /** Pokemon HP after battle - maps pokemon_id to remaining HP */
  pokemon_hp_after: Record<number, number>;
}

/**
 * Accumulated rewards from room bonuses
 */
export interface RaidAccumulatedRewards {
  /** Pokemon available for recruitment after raid */
  recruitment_pool: string[];
  /** Items earned from rooms */
  items: string[];
  /** Boss stat reduction percentage (from Boss Discovery rewards) */
  boss_stat_reduction: number;
  /** Increased chance for rare/epic boss (from Boss Discovery rewards) */
  boss_rarity_boost: number;
}

// ============================================================================
// Item Usage Types
// ============================================================================

import { ITEM_NAME } from "game/data/items/item-names";

/** Items that can be used during raid draft phase */
export const RAID_USABLE_ITEMS = [
  ITEM_NAME.POTION,
  ITEM_NAME.SUPER_POTION,
  ITEM_NAME.HYPER_POTION,
  ITEM_NAME.MAX_POTION,
  ITEM_NAME.REVIVE,
  ITEM_NAME.MAX_REVIVE,
] as const;

export type RaidUsableItem = (typeof RAID_USABLE_ITEMS)[number];

/** Item effects configuration for raid-usable items */
export const RAID_ITEM_EFFECTS: Record<
  RaidUsableItem,
  { type: "heal" | "revive"; value: number }
> = {
  [ITEM_NAME.POTION]: { type: "heal", value: 20 },
  [ITEM_NAME.SUPER_POTION]: { type: "heal", value: 50 },
  [ITEM_NAME.HYPER_POTION]: { type: "heal", value: 200 },
  [ITEM_NAME.MAX_POTION]: { type: "heal", value: Infinity },
  [ITEM_NAME.REVIVE]: { type: "revive", value: 0.5 },
  [ITEM_NAME.MAX_REVIVE]: { type: "revive", value: 1 },
};

/**
 * Item effect applied during draft phase
 */
export interface RaidItemEffect {
  /** HP restored (for potions) */
  hp_restored?: number;
  /** Whether the Pokemon was revived */
  revived?: boolean;
}

/**
 * Record of an item used during a draft phase
 */
export interface RaidItemUse {
  /** Which player used the item */
  player_role: PlayerRole;
  /** Item name used */
  item_name: ITEM_NAME;
  /** Target Pokemon ID */
  target_pokemon_id: number;
  /** Effect that was applied */
  effect_applied: RaidItemEffect;
  /** ISO timestamp when item was used */
  used_at: string;
}

// ============================================================================
// RAID_DATA Entry
// ============================================================================

/**
 * Main raid data entry
 *
 * DynamoDB Key:
 * - PK: raid_id
 * - SK: data_type = "RAID_DATA"
 */
export interface RaidDataEntry {
  /** Data type discriminator */
  data_type: "RAID_DATA";

  /** Unique identifier for this raid */
  raid_id: string;

  /** Raid name - Identifies which raid type this is (e.g., "Volcanic Caverns") */
  raid_name: string;

  /** Join code for players to join the lobby (6 chars) */
  join_code: string;

  /** Difficulty of the raid */
  difficulty: RaidDifficulty;

  /** Host player ID - the player who created the lobby */
  host_id: string;

  /** Number of players in this raid (1-4) */
  player_count: number;

  /** Step Function execution ARN - set when raid starts */
  step_function_execution_arn?: string;

  /** Step Function task token for connection wait phase */
  connection_task_token?: string;

  /** Player data - null slots mean no player in that position */
  players: {
    player_a: RaidPlayer | null;
    player_b: RaidPlayer | null;
    player_c: RaidPlayer | null;
    player_d: RaidPlayer | null;
  };

  /** WebSocket connection IDs for each player */
  connection_ids: {
    player_a: string | null;
    player_b: string | null;
    player_c: string | null;
    player_d: string | null;
  };

  /** Current status of the raid */
  status: RaidStatus;

  /** Current round index (0-9 for rooms, 10 for boss) */
  current_round_index: number;

  /** Current step within the round */
  current_step: RaidStepType;

  /** Rooms that have been completed */
  completed_rooms: RaidRoomOption[];

  /** Accumulated rewards from room bonuses */
  accumulated_rewards: RaidAccumulatedRewards;

  /** Visual ACK tracking for battle results */
  visual_ack?: {
    task_token: string;
    round_index: number;
    player_a: boolean;
    player_b: boolean;
    player_c: boolean;
    player_d: boolean;
  };

  /** Array of completed battles */
  battles: RaidBattleResult[];

  /** Final outcome - set when raid ends */
  outcome?: RaidOutcome;

  /** ISO timestamp when raid was created */
  created_at: string;

  /** ISO timestamp when raid was last updated */
  updated_at: string;

  /** TTL for automatic cleanup (Unix timestamp in seconds) */
  ttl: number;
}

// ============================================================================
// STEP Entry
// ============================================================================

export enum RaidStepType {
  ROOM_SELECTION = "room_selection",
  POKEMON_SELECTION = "pokemon_selection",
}

/**
 * Room selection step - stores the 4 room options and player votes
 */
export interface RaidRoomSelectionStep {
  /** Raid identifier */
  raid_id: string;

  /** Data type discriminator */
  data_type: `ROUND#${number}:STEP#${RaidStepType.ROOM_SELECTION}`;

  /** Round index this step belongs to */
  round_index: number;

  /** Type of step action */
  step_type: RaidStepType.ROOM_SELECTION;

  /** Duration in seconds for voting */
  step_duration_seconds: number;

  /** The 4 room options to vote on (indexed 0-3) */
  room_options: RaidRoomOption[];

  /** Player votes - index of the room they voted for (0-3), null if not voted */
  votes: {
    player_a: number | null;
    player_b: number | null;
    player_c: number | null;
    player_d: number | null;
  };

  /** Whether each player has locked in their vote */
  submitted: {
    player_a: boolean;
    player_b: boolean;
    player_c: boolean;
    player_d: boolean;
  };

  /** The selected room after voting completes */
  selected_room?: RaidRoomOption;

  /** Current state of this step */
  step_state: StepState;

  /** ISO timestamp when step started */
  step_started_at?: string;

  /** ISO timestamp when step completed */
  step_completed_at?: string;

  /** Step Function task token for resuming after all votes */
  action_task_token?: string;

  /** EventBridge Scheduler name for timeout */
  scheduler_name?: string;
}

/**
 * Configuration for a battle team slot
 */
export interface SlotConfig {
  /** Index of the slot (0-3 usually) */
  slot_index: number;
  /** Type of slot: personal (assigned to one player) or shared (anyone can pick) */
  type: "personal" | "shared";
  /** Player assigned to this slot (for personal slots) */
  assigned_to?: PlayerRole;
  /** Player who actually picked the pokemon (for shared slots or tracking) */
  picked_by?: PlayerRole;
  /** The picked pokemon ID */
  pokemon_id?: number;
}

/**
 * Pokemon selection step - stores player pokemon picks for battle
 */
export interface RaidPokemonSelectionStep {
  /** Raid identifier */
  raid_id: string;

  /** Data type discriminator */
  data_type: `ROUND#${number}:STEP#${RaidStepType.POKEMON_SELECTION}`;

  /** Round index this step belongs to */
  round_index: number;

  /** Type of step action */
  step_type: RaidStepType.POKEMON_SELECTION;

  /** Duration in seconds for draft */
  step_duration_seconds: number;

  /** Number of pokemon needed for battle (3 for 1-3 players, 4 for 4 players) */
  picks_required: number;

  /** Slot configuration for the battle team */
  slots: SlotConfig[];

  /** Pokemon IDs selected by each player */
  picks: {
    player_a: number[];
    player_b: number[];
    player_c: number[];
    player_d: number[];
  };

  /** Whether each player has locked in their picks */
  submitted: {
    player_a: boolean;
    player_b: boolean;
    player_c: boolean;
    player_d: boolean;
  };

  /** Items used during this draft phase */
  item_uses: RaidItemUse[];

  /** Final team after resolving over-selection (set when step completes) */
  final_team?: number[];

  /** Current state of this step */
  step_state: StepState;

  /** ISO timestamp when step started */
  step_started_at?: string;

  /** ISO timestamp when step completed */
  step_completed_at?: string;

  /** Step Function task token for resuming after all submissions */
  action_task_token?: string;

  /** EventBridge Scheduler name for timeout */
  scheduler_name?: string;
}

/** Union type for all step entries */
export type RaidStepEntry = RaidRoomSelectionStep | RaidPokemonSelectionStep;

// ============================================================================
// CONNECTION Entry
// ============================================================================

/**
 * WebSocket connection tracking entry
 *
 * DynamoDB Key:
 * - PK: raid_id
 * - SK: data_type = "CONNECTION#player_X"
 */
export interface RaidConnectionEntry {
  /** Raid identifier */
  raid_id: string;

  /** Data type discriminator with player role */
  data_type: `CONNECTION#${PlayerRole}`;

  /** Player ID (UUID) */
  player_id: string;

  /** WebSocket connection ID */
  connection_id: string;

  /** ISO timestamp when player connected */
  connected_at: string;

  /** ISO timestamp of last activity/heartbeat */
  last_seen_at: string;

  /** Number of times player disconnected during raid */
  disconnection_count: number;

  /** Total seconds player was disconnected */
  total_disconnected_seconds: number;

  /** ISO timestamp when player last disconnected (null if currently connected) */
  disconnected_at?: string;
}

// ============================================================================
// PLAYER_LOOKUP Entry
// ============================================================================

/**
 * Player lookup entry to find which raid a player is in.
 *
 * DynamoDB Key:
 * - PK: raid_id
 * - SK: data_type = "PLAYER_LOOKUP#<player_id>"
 */
export interface RaidPlayerLookupEntry {
  /** Raid identifier */
  raid_id: string;

  /** Data type discriminator with player ID */
  data_type: `PLAYER_LOOKUP#${string}`;

  /** Player ID (UUID) - Included as attribute for GSI indexing */
  player_id: string;

  /** ISO timestamp when lookup was created */
  created_at: string;

  /** TTL for automatic cleanup (Unix timestamp in seconds) */
  ttl: number;
}

// ============================================================================
// Union Type & Type Guards
// ============================================================================

/**
 * Discriminated union of all possible raid entry types
 */
export type RaidEntry =
  | RaidDataEntry
  | RaidStepEntry
  | RaidConnectionEntry
  | RaidPlayerLookupEntry;

/**
 * Type guard to check if entry is RaidDataEntry
 */
export function isRaidDataEntry(entry: RaidEntry): entry is RaidDataEntry {
  return entry.data_type === "RAID_DATA";
}

/**
 * Type guard to check if entry is a step entry
 */
export function isRaidStepEntry(entry: RaidEntry): entry is RaidStepEntry {
  return entry.data_type.startsWith("ROUND#");
}

/**
 * Type guard to check if entry is RaidRoomSelectionStep
 */
export function isRoomSelectionStep(
  entry: RaidEntry,
): entry is RaidRoomSelectionStep {
  return (
    entry.data_type.startsWith("ROUND#") &&
    entry.data_type.includes(RaidStepType.ROOM_SELECTION)
  );
}

/**
 * Type guard to check if entry is RaidPokemonSelectionStep
 */
export function isPokemonSelectionStep(
  entry: RaidEntry,
): entry is RaidPokemonSelectionStep {
  return (
    entry.data_type.startsWith("ROUND#") &&
    entry.data_type.includes(RaidStepType.POKEMON_SELECTION)
  );
}

/**
 * Type guard to check if entry is RaidConnectionEntry
 */
export function isRaidConnectionEntry(
  entry: RaidEntry,
): entry is RaidConnectionEntry {
  return entry.data_type.startsWith("CONNECTION#");
}

/**
 * Type guard to check if entry is RaidPlayerLookupEntry
 */
export function isRaidPlayerLookupEntry(
  entry: RaidEntry,
): entry is RaidPlayerLookupEntry {
  return entry.data_type.startsWith("PLAYER_LOOKUP#");
}

// ============================================================================
// Helper Types
// ============================================================================

/** Partial update type for RaidDataEntry */
export type RaidDataUpdate = Partial<
  Omit<RaidDataEntry, "raid_id" | "data_type">
>;

/** Partial update type for RaidRoomSelectionStep */
export type RaidRoomSelectionStepUpdate = Partial<
  Omit<RaidRoomSelectionStep, "raid_id" | "data_type">
>;

/** Partial update type for RaidPokemonSelectionStep */
export type RaidPokemonSelectionStepUpdate = Partial<
  Omit<RaidPokemonSelectionStep, "raid_id" | "data_type">
>;

/** Partial update type for RaidConnectionEntry */
export type RaidConnectionUpdate = Partial<
  Omit<RaidConnectionEntry, "raid_id" | "data_type">
>;

/** Generic update type for any step entry */
export type RaidStepUpdate =
  | RaidRoomSelectionStepUpdate
  | RaidPokemonSelectionStepUpdate;
